import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Card, Button, Empty, Spin, Tag, message } from 'antd';
import Spinner from '../../../../shared/designSystem/components/Spinner';
import { DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useQuery, useLazyQuery, useApolloClient } from 'react-apollo';
import GET_SIGNED_URL from '../../../../../core/GraphQl/Queries/GET_SIGNED_URL';
import moment from 'moment';

const IntegrationsModal = ({ visible, onClose, attachments }) => {
  const [attachmentsWithSignedUrls, setAttachmentsWithSignedUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const client = useApolloClient();

  const handleDownload = async (attachment) => {
    setDownloadingId(attachment.id);
    try {
      const { data } = await client.query({
        query: GET_SIGNED_URL,
        variables: { key: attachment.url },
        fetchPolicy: 'network-only', // Always fetch fresh URL
      });

      const link = document.createElement('a');
      link.href = data.getSignedURL;
      link.download = attachment.name || '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error fetching download URL:', error);
      message.error('Failed to download attachment. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    if (!attachments || attachments.length === 0) {
      setAttachmentsWithSignedUrls([]);
      return;
    }

    setLoading(true);

    const fetchSignedUrls = async () => {
      const responses = await Promise.all(
        attachments.map(async (attachment) => {
          const { data } = await client.query({
            query: GET_SIGNED_URL,
            variables: { key: attachment.url },
          });

          return { ...attachment, signedUrl: data.getSignedURL };
        })
      );

      setAttachmentsWithSignedUrls(responses);
      setLoading(false);
    };

    fetchSignedUrls();
  }, [attachments, client]);

  return (
    <Modal
      title="Campaign Attachments"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={600}
    >
      {loading || attachmentsWithSignedUrls.length < attachments.length ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spinner size="lg" />
          <p style={{ marginTop: '16px' }}>Loading attachments...</p>
        </div>
      ) : attachmentsWithSignedUrls && attachmentsWithSignedUrls.length > 0 ? (
        <div>
          <p>The following files are attached to this campaign:</p>
          {attachmentsWithSignedUrls.map((attachment) => (
            <Card key={attachment.id} style={{ marginBottom: '16px' }} size="small">
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <FilePdfOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>{attachment.name}</strong>
                    </div>
                    {attachment.description && (
                      <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>
                        {attachment.description}
                      </div>
                    )}
                    {attachment.tags && attachment.tags.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        {attachment.tags.map((tag, index) => (
                          <Tag key={index} size="small" style={{ marginRight: '4px' }}>
                            {tag}
                          </Tag>
                        ))}
                      </div>
                    )}
                    <div style={{ color: '#666', fontSize: '11px' }}>
                      {attachment.createdAt && (
                        <div>
                          Created: {moment(attachment.createdAt).format('MMM DD, YYYY [at] h:mm A')}
                        </div>
                      )}
                      {attachment.updatedAt && (
                        <div>
                          Updated: {moment(attachment.updatedAt).format('MMM DD, YYYY [at] h:mm A')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {attachment.signedUrl && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={downloadingId === attachment.id}
                    onClick={() => handleDownload(attachment)}
                  >
                    Download
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Empty description="No attachments found for this campaign" />
      )}
    </Modal>
  );
};

export default IntegrationsModal;
